import {
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  Eye,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { RequireAdmin } from '@/app/auth-guard'
import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { formatDateTime, formatNumber } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { kuniriIconWeight } from '@/lib/icons'

import { adminApi, type AdminDashboard, type AdminDashboardDays } from '../api'

type DashboardState =
  | { status: 'loading' }
  | { status: 'ready'; data: AdminDashboard }
  | { status: 'error'; error: unknown }

interface Metric {
  labelKey: string
  value: number | null
  to?: string
}

interface MetricSectionProps {
  titleKey: string
  subtitleKey: string
  metrics: Metric[]
  highlighted?: boolean
}

function MetricSection({
  titleKey,
  subtitleKey,
  metrics,
  highlighted = false,
}: MetricSectionProps) {
  const { t } = useTranslation()
  const locale = currentLocale()
  return (
    <section
      className={`rounded-[22px] p-3 sm:p-4 ${
        highlighted
          ? 'border border-[#EFD7B3] bg-[#FFFAF3]'
          : 'border border-transparent'
      }`}
    >
      <h2 className="font-head text-xl font-extrabold text-deep">
        {t(titleKey)}
      </h2>
      <p className="mt-0.5 text-sm leading-relaxed text-muted">
        {t(subtitleKey)}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2.5 min-[580px]:grid-cols-3 min-[900px]:grid-cols-4">
        {metrics.map((metric) => {
          const card = (
            <div className="flex min-h-[106px] flex-col justify-between rounded-[18px] border border-line bg-white p-3.5 text-left">
              <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1 text-xs font-bold text-muted">
                  {t(metric.labelKey)}
                </span>
                {metric.to ? (
                  <CaretRight
                    size={18}
                    weight="bold"
                    aria-hidden="true"
                    className="mt-0.5 shrink-0 text-teal"
                  />
                ) : null}
              </div>
              <p className="mt-2 font-body text-2xl leading-none font-bold text-deep">
                {metric.value == null
                  ? '—'
                  : formatNumber(metric.value, locale, {
                      maximumFractionDigits: 1,
                    })}
              </p>
            </div>
          )
          return metric.to ? (
            <Link
              key={metric.labelKey}
              to={metric.to}
              className="rounded-[18px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
            >
              {card}
            </Link>
          ) : (
            <div key={metric.labelKey}>{card}</div>
          )
        })}
      </div>
    </section>
  )
}

function DashboardCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: ReactNode
}) {
  return (
    <section className="rounded-[22px] border border-line bg-white p-5">
      <h2 className="font-head text-[19px] font-extrabold text-deep">{title}</h2>
      <p className="mt-0.5 text-sm leading-relaxed text-muted">{subtitle}</p>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function TrendChart({
  trend,
  label,
}: {
  trend: AdminDashboard['trend']
  label: string
}) {
  const { t } = useTranslation()
  if (trend.length === 0) {
    return <p className="text-sm text-muted">{t('admin.dashboard.trend.empty')}</p>
  }
  const max = Math.max(
    1,
    ...trend.map((point) => Math.max(point.registrations, point.activities)),
  )
  const width = 600
  const height = 180
  const inset = 8
  const chartWidth = width - inset * 2
  const chartHeight = height - inset * 2
  const pointAt = (index: number, value: number) => {
    const x =
      trend.length === 1
        ? width / 2
        : inset + (chartWidth * index) / (trend.length - 1)
    const y = inset + chartHeight - (chartHeight * value) / max
    return { x, y }
  }
  const series = (key: 'registrations' | 'activities') =>
    trend
      .map((point, index) => {
        const { x, y } = pointAt(index, point[key])
        return `${x},${y}`
      })
      .join(' ')

  return (
    <div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted">
          <span className="size-2.5 rounded-full bg-teal" aria-hidden="true" />
          {t('admin.dashboard.trend.registrations')}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted">
          <span
            className="size-2.5 rounded-full bg-[#3763A5]"
            aria-hidden="true"
          />
          {t('admin.dashboard.trend.activities')}
        </span>
      </div>
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${width} ${height}`}
        className="mt-3 h-[180px] w-full"
        preserveAspectRatio="none"
      >
        {[0, 1, 2, 3].map((line) => {
          const y = inset + (chartHeight * line) / 3
          return (
            <line
              key={line}
              x1={inset}
              x2={width - inset}
              y1={y}
              y2={y}
              stroke="var(--line)"
              strokeWidth="1"
            />
          )
        })}
        <polyline
          points={series('registrations')}
          fill="none"
          stroke="var(--teal)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points={series('activities')}
          fill="none"
          stroke="#3763A5"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-muted">
        <span>{shortDate(trend[0]?.day)}</span>
        <span>{shortDate(trend[trend.length - 1]?.day)}</span>
      </div>
    </div>
  )
}

function CountBars({
  rows,
  labelKey,
  colorKey,
  emptyText,
  translateLabel = false,
}: {
  rows: readonly object[]
  labelKey: string
  colorKey?: string
  emptyText: string
  translateLabel?: boolean
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted">{emptyText}</p>
  }
  const items = rows as Array<Record<string, unknown>>
  const max = Math.max(
    1,
    ...items.map((row) => Number(row.count) || 0),
  )
  return (
    <ul className="space-y-3.5">
      {items.map((row, index) => {
        const count = Number(row.count) || 0
        const rawLabel = String(row[labelKey] ?? '—')
        const label = translateLabel ? `admin.status.${rawLabel}` : rawLabel
        const color =
          colorKey && /^#[0-9a-f]{6}$/i.test(String(row[colorKey]))
            ? String(row[colorKey])
            : 'var(--teal)'
        return (
          <li key={`${rawLabel}-${index}`}>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate text-deep">
                {translateLabel ? <BarLabel labelKey={label} /> : label}
              </span>
              <span className="font-bold text-deep">{count}</span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-pill bg-mint">
              <div
                className="h-full rounded-pill"
                style={{
                  width: `${Math.max(4, (count / max) * 100)}%`,
                  backgroundColor: color,
                }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function BarLabel({ labelKey }: { labelKey: string }) {
  const { t } = useTranslation()
  return <>{t(labelKey)}</>
}

function shortDate(value: string | undefined): string {
  if (!value) return ''
  const parsed = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return ''
  return `${String(parsed.getUTCDate()).padStart(2, '0')}/${String(
    parsed.getUTCMonth() + 1,
  ).padStart(2, '0')}`
}

function formatGeneratedAt(value: string | undefined, locale: 'it' | 'en') {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return formatDateTime(parsed, locale)
}

/**
 * Read-only in-app admin dashboard (D-055). It mirrors the Flutter overview and
 * links every drillable KPI to a filtered record list. No moderation actions are
 * exposed here.
 */
export function AdminDashboardScreen() {
  const { t } = useTranslation()
  const locale = currentLocale()
  const [days, setDays] = useState<AdminDashboardDays>(30)
  const [state, setState] = useState<DashboardState>({ status: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    adminApi
      .getDashboard(days, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setState({ status: 'ready', data })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setState({ status: 'error', error })
      })
    return () => controller.abort()
  }, [days, reloadKey])

  const refresh = useCallback(() => {
    setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }, [])

  const kpis = state.status === 'ready' ? state.data.kpis : null
  const userLink = (filters: Record<string, string>, labelKey: string) =>
    `/admin-dashboard/users?${new URLSearchParams({
      ...filters,
      title: labelKey,
    }).toString()}`
  const activityLink = (filters: Record<string, string>, labelKey: string) =>
    `/admin-dashboard/activities?${new URLSearchParams({
      ...filters,
      title: labelKey,
    }).toString()}`

  return (
    <RequireAdmin>
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 pt-[calc(var(--safe-top)+16px)] pb-[calc(2rem+var(--safe-bottom))] md:px-8 md:pt-8"
      >
        <div className="mx-auto w-full max-w-[1120px]">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/profile" className="btn-ghost min-h-11 px-2 text-sm">
              <CaretLeft size={19} weight={kuniriIconWeight} aria-hidden="true" />
              {t('admin.dashboard.back')}
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="admin-period">
                {t('admin.dashboard.periodLabel')}
              </label>
              <select
                id="admin-period"
                className="field-input h-11 w-auto py-0 text-sm"
                value={days}
                onChange={(event) =>
                  setDays(Number(event.target.value) as AdminDashboardDays)
                }
              >
                {[7, 30, 90].map((value) => (
                  <option key={value} value={value}>
                    {t('admin.dashboard.period', { count: value })}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary min-h-11 px-3 text-sm"
                onClick={refresh}
                aria-label={t('admin.dashboard.refresh')}
              >
                <ArrowClockwise
                  size={19}
                  weight={kuniriIconWeight}
                  aria-hidden="true"
                />
                {t('admin.dashboard.refreshShort')}
              </button>
            </div>
          </header>

          <h1 className="mt-6 font-head text-[clamp(1.7rem,4vw,2.1rem)] font-extrabold text-deep">
            {t('admin.dashboard.heading')}
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-muted">
            {t('admin.dashboard.subtitle', {
              date: formatGeneratedAt(
                state.status === 'ready' ? state.data.generatedAt : undefined,
                locale,
              ),
              days,
            })}
          </p>

          {state.status === 'loading' ? (
            <div className="mt-6">
              <LoadingState labelKey="admin.dashboard.loading" />
            </div>
          ) : state.status === 'error' ? (
            <div className="mt-6">
              <ErrorState
                error={state.error}
                titleKey="admin.dashboard.errorTitle"
                bodyKey="admin.dashboard.errorBody"
                onRetry={refresh}
                retryLabelKey="admin.dashboard.retry"
              />
            </div>
          ) : state.status === 'ready' && kpis ? (
            <div className="mt-6 space-y-4">
              <MetricSection
                titleKey="admin.dashboard.sections.users.title"
                subtitleKey="admin.dashboard.sections.users.subtitle"
                metrics={[
                  {
                    labelKey: 'admin.dashboard.metrics.activeAccounts',
                    value: kpis.activeAccounts,
                    to: userLink({ status: 'active' }, 'admin.dashboard.metrics.activeAccounts'),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.verifiedAccounts',
                    value: kpis.verifiedAccounts,
                    to: userLink(
                      { status: 'active', verification: 'verified' },
                      'admin.dashboard.metrics.verifiedAccounts',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.unverifiedAccounts',
                    value: kpis.unverifiedAccounts,
                    to: userLink(
                      { status: 'active', verification: 'unverified' },
                      'admin.dashboard.metrics.unverifiedAccounts',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.suspendedAccounts',
                    value: kpis.suspendedAccounts,
                    to: userLink(
                      { status: 'suspended' },
                      'admin.dashboard.metrics.suspendedAccounts',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.deletedAccounts',
                    value: kpis.deletedAccounts,
                    to: userLink(
                      { status: 'deleted' },
                      'admin.dashboard.metrics.deletedAccounts',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.registrations',
                    value: kpis.registrations,
                    to: userLink(
                      { createdDays: String(days) },
                      'admin.dashboard.metrics.registrations',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.usersWithActions',
                    value: kpis.usersWithActions,
                    to: userLink(
                      { status: 'active' },
                      'admin.dashboard.metrics.usersWithActions',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.activityCreators',
                    value: kpis.activityCreators,
                    to: activityLink(
                      { createdDays: String(days) },
                      'admin.dashboard.metrics.activityCreators',
                    ),
                  },
                ]}
              />

              <MetricSection
                titleKey="admin.dashboard.sections.activities.title"
                subtitleKey="admin.dashboard.sections.activities.subtitle"
                metrics={[
                  {
                    labelKey: 'admin.dashboard.metrics.upcomingActivities',
                    value: kpis.upcomingActivities,
                    to: activityLink(
                      { period: 'upcoming' },
                      'admin.dashboard.metrics.upcomingActivities',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.activitiesCreated',
                    value: kpis.activitiesCreated,
                    to: activityLink(
                      { createdDays: String(days) },
                      'admin.dashboard.metrics.activitiesCreated',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.completedActivities',
                    value: kpis.completedActivities,
                    to: activityLink(
                      { status: 'completed', period: 'past' },
                      'admin.dashboard.metrics.completedActivities',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.participatingUsers',
                    value: kpis.participatingUsers,
                    to: activityLink(
                      { period: 'past' },
                      'admin.dashboard.metrics.participatingUsers',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.confirmedParticipations',
                    value: kpis.confirmedParticipations,
                    to: activityLink(
                      { period: 'past' },
                      'admin.dashboard.metrics.confirmedParticipations',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.pendingRequests',
                    value: kpis.pendingRequests,
                    to: activityLink(
                      { pendingRequests: 'true' },
                      'admin.dashboard.metrics.pendingRequests',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.occupancyPercent',
                    value: kpis.occupancyPercent,
                    to: activityLink(
                      { period: 'upcoming' },
                      'admin.dashboard.metrics.occupancyPercent',
                    ),
                  },
                  {
                    labelKey: 'admin.dashboard.metrics.openReports',
                    value: kpis.openReports,
                  },
                ]}
              />

              <MetricSection
                titleKey="admin.dashboard.sections.cancellations.title"
                subtitleKey="admin.dashboard.sections.cancellations.subtitle"
                highlighted
                metrics={[
                  {
                    labelKey: 'admin.dashboard.metrics.cancellations',
                    value: kpis.cancellations,
                    to: activityLink(
                      { status: 'cancelled' },
                      'admin.dashboard.metrics.cancellations',
                    ),
                  },
                ]}
              />

              <DashboardCard
                title={t('admin.dashboard.trend.title')}
                subtitle={t('admin.dashboard.trend.subtitle')}
              >
                <TrendChart
                  trend={state.data.trend}
                  label={t('admin.dashboard.trend.label')}
                />
              </DashboardCard>

              <DashboardCard
                title={t('admin.dashboard.categories.title')}
                subtitle={t('admin.dashboard.categories.subtitle', { days })}
              >
                <CountBars
                  rows={state.data.categories}
                  labelKey="name"
                  colorKey="color"
                  emptyText={t('admin.dashboard.categories.empty')}
                />
              </DashboardCard>

              <DashboardCard
                title={t('admin.dashboard.reports.title')}
                subtitle={t('admin.dashboard.reports.subtitle')}
              >
                <CountBars
                  rows={state.data.reports}
                  labelKey="status"
                  emptyText={t('admin.dashboard.reports.empty')}
                  translateLabel
                />
              </DashboardCard>

              <section className="rounded-[22px] border border-[#EFD7B3] bg-[#FFFAF3] p-5">
                <h2 className="flex items-center gap-2 font-head text-lg font-extrabold text-deep">
                  <Eye
                    size={20}
                    weight={kuniriIconWeight}
                    aria-hidden="true"
                    className="text-[#895000]"
                  />
                  {t('admin.dashboard.watch.title')}
                </h2>
                <div className="mt-3 space-y-1.5 text-sm leading-relaxed text-deep">
                  <p>
                    {t('admin.dashboard.watch.pending', {
                      count: kpis.pendingRequests,
                    })}
                  </p>
                  <p>
                    {t('admin.dashboard.watch.suspended', {
                      count: kpis.suspendedAccounts,
                    })}
                  </p>
                  <p>
                    {kpis.oldestReportAt
                      ? t('admin.dashboard.watch.oldestReport', {
                          date: formatGeneratedAt(
                            kpis.oldestReportAt,
                            locale,
                          ),
                        })
                      : t('admin.dashboard.watch.noReports')}
                  </p>
                </div>
              </section>

              <p className="px-2 text-center text-xs leading-relaxed text-muted">
                {t('admin.dashboard.footer')}
              </p>
            </div>
          ) : null}
        </div>
      </main>
    </RequireAdmin>
  )
}
